import { useBusinessOnboardingHost } from "@/business-onboarding/onboarding-host-context";
import { formatDate, label } from "@/admin/model";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { RefreshCw } from "lucide-react";
import { useAdminStore } from "@/stores/adminStore";
import { rootMetrics } from "@/admin/model";
import { AnalyticsMetrics, CompanyLink, Metrics, RecentActivity } from "@/admin/components";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "@/components/DataState";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/admin/")({ component: AdminOverview });
function AdminOverview() {
  const state = useAdminStore();
  const { openOnboarding } = useBusinessOnboardingHost();
  useEffect(() => {
    if (state.uid) void useAdminStore.getState().refreshAnalytics();
  }, [state.uid]);
  const metrics = rootMetrics(state.companies);
  const loaded = state.status === "ready" || state.status === "empty";
  const recent = [...state.companies]
    .filter((c) => c.createdAt !== null)
    .sort((a, b) => b.createdAt! - a.createdAt!)
    .slice(0, 6);
  return (
    <>
      <PageHeader title="Overview" subtitle="Company operations and system-wide activity." />
      <Button variant="outline" className="w-fit" aria-haspopup="dialog" onClick={() => openOnboarding({ freshForAdmin: true })}>
        Try business onboarding (temporary)
      </Button>
      {state.status === "error" ? (
        <ErrorState error={state.error} />
      ) : !loaded ? (
        <LoadingState label="Loading companies…" />
      ) : (
        <Metrics
          values={[
            ["Total Companies", metrics.total],
            ["Managed Companies", metrics.managed],
            ["Design Partners", metrics.partners],
            ["Commercially Active Companies", metrics.active],
          ]}
        />
      )}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-extrabold">System activity</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              All stored leads and discoveries, including persisted previews. Conversion is leads
              divided by discoveries.
            </p>
            {state.analytics && (
              <p className="mt-1 text-xs text-muted-foreground">
                Updated {formatDate(state.analytics.updatedAt)}
                {state.cached ? " · Cached / previous result" : " · Fresh result"}
                {state.refreshing ? " · Refreshing…" : ""}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={state.refreshing || !state.uid}
            onClick={() => void state.refreshAnalytics()}
          >
            <RefreshCw className={`size-4 ${state.refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
        {state.analyticsError && (
          <p
            role="alert"
            className="rounded-xl border border-warning/20 bg-warning-soft p-3 text-sm"
          >
            Analytics could not refresh.{" "}
            {state.analytics
              ? "The previous result is still shown."
              : "No analytics are available yet."}{" "}
            {state.analyticsError.message}
          </p>
        )}
        {state.analytics ? (
          <AnalyticsMetrics value={state.analytics} />
        ) : state.refreshing ? (
          <LoadingState label="Loading analytics…" />
        ) : (
          <EmptyState message="Analytics have not loaded." />
        )}
      </section>
      <div className="grid items-start gap-5 lg:grid-cols-2">
        <section className="surface-elevated p-5">
          <h2 className="text-lg font-extrabold">Recent Companies</h2>
          <p className="mt-1 text-xs text-muted-foreground">Ordered by creation time.</p>
          {!loaded ? (
            <p className="mt-4 text-sm text-muted-foreground">Company data is not available yet.</p>
          ) : recent.length ? (
            <ul className="mt-3 divide-y divide-border">
              {recent.map((company) => (
                <li
                  key={company.id}
                  className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm"
                >
                  <CompanyLink company={company} profile={state.profiles[company.id]} />
                  <time className="text-xs text-muted-foreground">
                    {formatDate(company.createdAt)}
                  </time>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState message="No companies with a creation timestamp." />
          )}
        </section>
        {state.analytics && (
          <RecentActivity analytics={state.analytics} companies={state.companies} />
        )}
      </div>
    </>
  );
}
